// @flow

import React, { Component } from 'react';
import { connect } from 'react-redux';
import AdCreditsUpsell from 'app/common/react/components/growth/smb/OverviewCreatorPromotablePinsGrid/AdCreditsUpsell';
import AddPinRep from 'app/common/react/components/save/AddPin/AddPinRep';
import BoostTextExperiment from 'app/common/react/components/Business/BoostTextExperiment';
import EducationModules from 'app/common/react/components/EducationModules/EducationModules';
import Experience from 'app/common/react/components/experiences/Experience/Experience';
import i18n from 'app/common/lib/i18n';
import ProfilePromotablePinCard from 'app/common/react/components/profile/ProfilePromotablePinCard/ProfilePromotablePinCard';
import templateConst from 'app/common/lib/templateConst';
import TimeSpent from 'app/common/lib/react/TimeSpent';
import type { Experience as ExperienceType } from 'app/packages/pinterest-flow-types/experience';
import type { ExperienceClientProps } from 'app/common/lib/react/experiences/withExperienceClient';
import type { ListCreatorAnalytics } from 'app/common/react/components/closeup/PinAnalytics';
import type { PinProps } from 'app/common/react/components/DisplayPinImage/DisplayPinImage';
import type { Router } from 'app/common/lib/ReactRouter/types';
import type { State as BoardsState } from 'app/common/redux/reducers/boards';
import type { State } from 'app/common/redux/reducers/types';
import withExperienceClient from 'app/common/lib/react/experiences/withExperienceClient';
import withPlacement from 'app/common/lib/react/experiences/withPlacement';
import { advertiserIntent } from 'app/common/lib/BusinessUtil';
import { compose } from 'redux';
import { interpolateNamedTemplate } from 'app/common/lib/react/interpolate';
import { sessionStorage } from 'app/common/lib/Storage';
import { SingleTreatmentExp } from 'app/common/lib/experiment';
import { withRouter } from 'react-router';
import withResource, { type Resource } from 'app/common/lib/react/withResource';
import withViewer, { type Viewer } from 'app/common/lib/react/withViewer';
import {
  withRequestContext,
  type RequestContextType,
} from 'app/common/react/components/RequestContext';
import { Box, Button, Heading, Icon, Link, Spinner, Text } from 'gestalt';

export type PinWithAnalytics = PinProps & {
  aggregated_pin_data: {
    creator_analytics?: ListCreatorAnalytics,
  },
  creator_analytics: ListCreatorAnalytics,
  id: string,
  link: string,
};

type PinData = {|
  clickthrough_count: number,
  id: number,
  impression_count: number,
  pin: ?PinWithAnalytics,
  repin_count: number,
|};

type HOCProps = {|
  boards: BoardsState,
|};

type OwnProps = {|
  experience: ExperienceType,
  id: string,
  pins: Resource<Array<PinData>>,
  requestContext: RequestContextType,
  router: Router,
  showCreateFlow: (string) => void,
  username: string,
  viewer: Viewer,
|};

type ConnectedProps = {| ...HOCProps, ...OwnProps |};

type Props = ExperienceClientProps & ConnectedProps;

type ComponentState = {
  boards: BoardsState,
  showPromotedPins: boolean,
};

const NUM_PROMOTABLE_PINS = 3;
const QUALIFYING_SPEND = 30;
const REFUND_CREDIT_AMOUNT = 20;

const getToggleButtonPreferenceKey = (userId: string) => `showPromotedPinsFor${userId}`;
const getToggleButtonTimestampKey = (userId: string) => `showPromotedPinsTimstampFor${userId}`;

const getInitialShowPromotedPinState = (userId) => {
  if (sessionStorage.getItem(getToggleButtonPreferenceKey(userId)) === null) {
    return true;
  }

  return sessionStorage.getItem(getToggleButtonPreferenceKey(userId));
};

const showAdsCreditReminder = (experience) =>
  experience &&
  (experience.experience_id === PCONST.thrift.Experience.MARKETING_OFFER_PROFILE_REMINDER ||
    experience.experience_id === PCONST.thrift.Experience.WEB_AD_CREDITS_NUX_REMINDER ||
    experience.experience_id === PCONST.thrift.Experience.WEB_AD_CREDITS_EXISTING_REMINDER);

const showAdsCreditExperience = (experience) =>
  experience &&
  (experience.experience_id === PCONST.thrift.Experience.WEB_AD_CREDITS_NUX ||
    experience.experience_id === PCONST.thrift.Experience.WEB_AD_CREDITS_EXISTING);

export class OverviewCreatorPromotablePinsGrid extends Component<Props, ComponentState> {
  constructor(props: Props) {
    super(props);
    const { id: userId } = this.props;

    this.state = {
      boards: this.props.boards,
      showPromotedPins: getInitialShowPromotedPinState(userId),
    };
  }

  static getDerivedStateFromProps(props: Props, state: ComponentState) {
    /* Connect to boards state to detect newly created/deleted pins  */
    const updatePins = Object.keys(props.boards.content).some((key) => {
      const newBoard = props.boards.content[key] || {};
      const oldBoard = state.boards.content[key] || {};
      return newBoard.pin_count !== oldBoard.pin_count;
    });
    if (updatePins) {
      props.pins.refreshData();
      return { boards: props.boards };
    }
    return null;
  }

  handlePinCreateClick = () => {
    const { router } = this.props;
    router.push('/pin-builder/');
  };

  handlePromotedPinsToggle = () => {
    const { id: userId } = this.props;
    const newToggleState = !this.state.showPromotedPins;
    const preferenceKey = getToggleButtonPreferenceKey(userId);
    const timestampKey = getToggleButtonTimestampKey(userId);
    const newToggleTime = new Date().getTime();

    // NOTE: adding a timestamp here in the event we ever want to do reset the preference
    // after a certain amount of time @jlumarie
    sessionStorage.setItem(timestampKey, newToggleTime);
    sessionStorage.setItem(preferenceKey, newToggleState);

    this.setState({
      showPromotedPins: newToggleState,
    });
  };

  renderToggleButton = (buttonText: string) => (
    <Box display="flex" marginBottom={4}>
      <Button
        color="gray"
        size="sm"
        text={buttonText}
        onClick={this.handlePromotedPinsToggle}
        inline
      />
    </Box>
  );

  // $FlowFixMe Flow v0.104.0 upgrade supression.
  renderPinsSection(pinsData: Array<*>) {
    const { experience } = this.props;
    const { showPromotedPins } = this.state;
    const toggleButtonText = showPromotedPins
      ? i18n._('Hide', 'Hide the promoted pins grid from the user')
      : i18n._('Show', 'Show the promoted pins grid to the user');

    const slicedPinsData = pinsData
      .filter((pinData) => pinData.pin !== null)
      .slice(0, NUM_PROMOTABLE_PINS);

    const show90DaysPinStatsOnGrid = new SingleTreatmentExp(
      this.props.requestContext,
      'px_pinstats_90_day_default_on_grid',
    ).enabled();

    const promotablePins = slicedPinsData.map((pinData, i) => (
      <ProfilePromotablePinCard
        component={PCONST.thrift.ComponentType.USER_PROMOTABLE_PINS}
        key={pinData.id}
        grid_index={i + 1}
        isShort
        pin={pinData}
        view={PCONST.thrift.ViewType.USER}
        viewParameter={PCONST.thrift.ViewParameterType.USER_OVERVIEW}
        show90DaysStats={show90DaysPinStatsOnGrid}
      />
    ));

    const createPinRep = (
      <div key={0}>
        <Box height={236} width={236} marginRight={4}>
          <AddPinRep ctaText={i18n._('Create a new Pin', 'label to open create pin modal')} />
        </Box>
      </div>
    );
    const row = [promotablePins.length < NUM_PROMOTABLE_PINS && createPinRep, ...promotablePins];

    return (
      <React.Fragment>
        <Box
          alignContent="end"
          alignItems="center"
          display="flex"
          justifyContent="between"
          marginTop={4}
          paddingX={4}
        >
          {showAdsCreditReminder(experience) ? (
            <Experience
              placementId={PCONST.thrift.Placement.WEB_PROMOTED_PINS_SECTION}
              experienceIds={[experience.experience_id]}
            >
              {() => this.renderAdsCreditsReminder()}
            </Experience>
          ) : (
            <Box marginBottom={6}>
              <Text size="xl" weight="bold">
                <BoostTextExperiment>
                  {({ anyEnabled }) =>
                    anyEnabled
                      ? i18n._('Boost a Pin', 'Heading for boostable pins section on own profile')
                      : i18n._(
                          'Promote a Pin',
                          'Heading for promotable pins section on own profile',
                        )
                  }
                </BoostTextExperiment>
              </Text>
              <Text size="lg">
                {interpolateNamedTemplate(
                  i18n._(
                    'Reach more people with Pinterest Ads. {{ learnMore }}',
                    'Subheading for promotable pins section on own profile',
                  ),
                  {
                    learnMore: (
                      <Link
                        inline
                        href={`${
                          templateConst.settings.RELATIVE_PARTNER_SITE_URL
                        }why-advertise-on-pinterest`}
                        target="blank"
                        key={0}
                      >
                        <Text inline size="lg" weight="bold">
                          {i18n._('Learn more', 'Link to business site')}
                        </Text>
                      </Link>
                    ),
                  },
                )}
              </Text>
            </Box>
          )}
          {this.renderToggleButton(toggleButtonText)}
        </Box>
        {this.state.showPromotedPins && (
          <Box direction="row" display="flex" marginLeft={4}>
            {row}
          </Box>
        )}
      </React.Fragment>
    );
  }

  renderAdsCreditsReminder() {
    const {
      experience: {
        display_data: { body_text: bodyText, header_text: headerText },
      },
    } = this.props;

    return (
      <Box marginBottom={5}>
        <Text color="red" size="xl" weight="bold">
          {headerText}
        </Text>
        <Text size="xl">{bodyText}</Text>
      </Box>
    );
  }

  renderEmptyState() {
    const { viewer, requestContext } = this.props;
    const educationSteps = [
      {
        number: 1,
        header: i18n._('Use a vertical image', '1. Header - First pin create tip for a business'),
        body: i18n._(
          'Use a vertical (portrait orientation) photo with a 2:3 aspect ratio.',
          '1. Body - First pin create tip for a business',
        ),
      },
      {
        number: 2,
        header: i18n._('Add a description', '2. Header - First pin create tip for a business'),
        body: i18n._(
          'Write a description to give your image context.',
          '2. Body - First pin create tip for a business',
        ),
      },
      {
        number: 3,
        header: i18n._('Include a URL', '3. Header - First pin create tip for a business'),
        body: i18n._(
          'Adding a URL helps direct people to your site.',
          '3. Body - First pin create tip for a business',
        ),
      },
    ];

    const eligibleForPinEduExp =
      viewer.partner &&
      viewer.partner.advertising_intent === advertiserIntent.YES &&
      new SingleTreatmentExp(requestContext, 'smb_pin_builder_edu_banner').enabled();

    return (
      <Box display="flex" justifyContent="center">
        <Box paddingX={10} marginTop={6}>
          <Heading size="sm">
            {eligibleForPinEduExp
              ? i18n._(
                  'Create Pins to share products or ideas',
                  'First pin create header on profile page for yes adverting intent',
                )
              : i18n._('Tips for creating great Pins', 'Promoted pins section empty state header')}
          </Heading>
          {eligibleForPinEduExp && (
            <Box marginTop={1}>
              <Text size="xl">
                {i18n._(
                  'To create an ad, you can promote this Pin later',
                  'Subtitle educating users a pin is needed to create an ad',
                )}
              </Text>
            </Box>
          )}
          <Box display="flex" direction="column" justifyContent="between" height={275}>
            <Box display="flex" justifyContent="around" alignItems="baseline" marginTop={6}>
              <EducationModules isPromotedSection educationSteps={educationSteps} />
            </Box>
            <Button
              color="red"
              text={i18n._('Create Pin', 'label to open create pin modal')}
              onClick={this.handlePinCreateClick}
            />
          </Box>
        </Box>
      </Box>
    );
  }

  render() {
    const { pins, experience, experienceClient } = this.props;

    const { isLoaded, data: pinsData } = pins;
    if (!isLoaded) {
      return (
        <Box alignItems="center" display="flex" justifyContent="center" paddingY={6}>
          <Spinner
            accessibilityLabel={i18n._(
              'Loading Promotable Pins on profile',
              'Accessibility label for loading spinner for promotable pins section on profile',
            )}
            show
          />
        </Box>
      );
    }

    return (
      <TimeSpent
        view={PCONST.thrift.ViewType.USER}
        viewParameter={PCONST.thrift.ViewParameterType.USER_OVERVIEW}
        component={PCONST.thrift.ComponentType.USER_PROMOTABLE_PINS}
      >
        <Box justifyContent="center" display="flex">
          <Box
            direction="column"
            display="flex"
            dangerouslySetInlineStyle={{
              __style: { borderWidth: 2, borderStyle: 'solid', borderColor: '#efefef' },
            }}
            marginStart={3}
            marginEnd={3}
            width="100%"
            maxWidth={776}
            shape="rounded"
          >
            {showAdsCreditExperience(experience) ? (
              <Experience
                placementId={PCONST.thrift.Placement.WEB_PROMOTED_PINS_SECTION}
                experienceIds={[experience.experience_id]}
              >
                {(props) => (
                  <AdCreditsUpsell
                    spendAmount={QUALIFYING_SPEND}
                    refundAmount={REFUND_CREDIT_AMOUNT}
                    experienceClient={experienceClient}
                    {...props}
                  />
                )}
              </Experience>
            ) : (
              <React.Fragment>
                {!pinsData || !pinsData.length
                  ? this.renderEmptyState()
                  : this.renderPinsSection(pinsData)}
              </React.Fragment>
            )}
            {this.state.showPromotedPins && (
              <Box
                alignSelf="center"
                alignItems="center"
                marginTop={7}
                marginBottom={6}
                display="flex"
              >
                <Box
                  color="lightWash"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  shape="circle"
                  marginRight={2}
                  height={32}
                  width={32}
                >
                  <Icon
                    color="darkGray"
                    icon="lock"
                    inline
                    size={16}
                    accessibilityLabel={i18n._(
                      'Lock icon',
                      'Accessibility label for lock icon in promotable pins section on own profile',
                    )}
                  />
                </Box>
                <Text size="lg">
                  {i18n._(
                    'This is only visible to you',
                    'Message below promotable pins section on own profile',
                  )}
                </Text>
              </Box>
            )}
          </Box>
        </Box>
      </TimeSpent>
    );
  }
}

const mapStateToProps = ({ boards }: State) => ({ boards });

export default compose(
  connect<ConnectedProps, OwnProps, _, _, _, _>(
    mapStateToProps,
    () => ({}),
  ),
  withExperienceClient,
  withRequestContext,
  withRouter,
  withViewer,
  withPlacement(PCONST.thrift.Placement.WEB_PROMOTED_PINS_SECTION),
  withResource(
    {
      name: 'PromotablePinsResource',
      key: 'pins',
      options: (props) => ({ username: props.username }),
    },
    { allowStale: false },
  ),
)(OverviewCreatorPromotablePinsGrid);
